// The MIT License (MIT)
// Copyright (c) 2016-present Leon Shi
// Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated
// documentation files (the "Software"), to deal in the Software without restriction, including without limitation the
// rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to
// permit persons to whom the Software is furnished to do so, subject to the following conditions:
// The above copyright notice and this permission notice shall be included in all copies or substantial portions of the
// Software.
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE
// WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS
// OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR
// OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.

// Copied from: https://msazure.visualstudio.com/DefaultCollection/One/_git/Azure-Kusto-WebUX?version=GCed7b1f79543169fd437e54138341ac63136ebf2b&path=%2Fkustoweb%2Fsrc%2FOpenSource%2FReactMonacoEditor%2FReactMonacoEditor.tsx

/* eslint-disable */
// @ts-nocheck

// Adapted from from https://github.com/superRaytin/react-monaco-editor/blob/master/src/index.js
// Goal: Monaco has a dependency model based on umd module and assumes a specific directory structure
// for where it looks for dependencies. This isn't compatible with the way our application is doing
// modules and bundles (webpack, es modules).
// The component will first load require.js (to pull in umd modules), and then load the monaco editor.
// It will then load the kusto specific parts of the monaco editor.
import * as React from 'react';
import * as _ from 'lodash';

export interface ModelInfo {
    modelId: string;
    allModelIds: string[];
}

export interface ReactMonacoEditorProps {
    width?: string | number;
    height?: string | number;
    value?: string;
    defaultValue?: string;
    language?: string;
    theme?: string;
    options?: monaco.editor.IEditorConstructionOptions;
    editorWillMount?: (mon: typeof monaco) => void;
    editorDidMount?: (ed: monaco.editor.IStandaloneCodeEditor, mon: typeof monaco) => void;
    onChange?: (value: string, event: monaco.editor.IModelContentChangedEvent) => void;
    requireConfig?: { url?: string; paths?: { vs?: string } };
    modelInfo?: ModelInfo;
    context?: any;
}

const defaultConfig = {
    waitSeconds: 30,
    url: `./require.js`,
    paths: {
        vs: `./monaco-editor/min/vs`,
    },
};
export const DefaultKustoEditorConfig: Readonly<typeof defaultConfig> = defaultConfig;

interface ModelAndView {
    model: monaco.editor.ITextModel;
    viewState?: monaco.editor.ICodeEditorViewState;
}

export class ReactMonacoEditor extends React.Component<ReactMonacoEditorProps, {}> {
    public static defaultProps = {
        width: '100%',
        height: '100%',
        value: null,
        defaultValue: '',
        language: 'javascript',
        theme: null,
        options: {},
        editorDidMount: () => undefined,
        editorWillMount: () => undefined,
        onChange: () => undefined,
        requireConfig: DefaultKustoEditorConfig,
        modelId: '',
        allModelIds: [],
    };

    private containerElement: any;
    private currentValue?: string;
    private preventTriggerChangeEvent: boolean = false;
    private editor?: monaco.editor.IStandaloneCodeEditor = undefined;

    private resolveMonacoEditorMountPromise?: (value?: {} | PromiseLike<{}> | undefined) => void;
    // In order to prevent cases where we call monaco api without it being initialized yet,
    // we provide a promise for when it's initialized.
    private monacoEditorMountPromise = new Promise((resolve) => {
        this.resolveMonacoEditorMountPromise = resolve;
    });

    private models: { [modelId: string]: ModelAndView } = {};

    constructor(props: ReactMonacoEditorProps) {
        super(props);
        this.containerElement = undefined;
        this.currentValue = props.value;
    }

    componentDidMount() {
        this.afterViewInit();
    }

    componentDidUpdate(prevProps: ReactMonacoEditorProps) {
        this.handleModelChange(prevProps);
        this.handleTextChange(prevProps);
        this.handleLanguageChange(prevProps);
        this.handleThemeChange(prevProps);
        this.handleSettingsChange(prevProps);
    }

    componentWillUnmount() {
        this.delayedDestroyMonaco();
    }

    editorWillMount(mon: typeof monaco) {
        const { editorWillMount } = this.props;
        if (editorWillMount) {
            editorWillMount(mon);
        }
    }

    editorDidMount(editor: monaco.editor.IStandaloneCodeEditor, mon: typeof monaco) {
        if (this.props.editorDidMount) {
            this.props.editorDidMount(editor, mon);
        }

        editor.onDidChangeModelContent((event: monaco.editor.IModelContentChangedEvent) => {
            const value = editor.getValue({ lineEnding: '\n', preserveBOM: false });

            // Always refer to the latest value
            this.currentValue = value;

            // Only invoking when user input changed
            if (!this.preventTriggerChangeEvent && this.props.onChange) {
                this.props.onChange(value, event);
            }
        });
    }

    render() {
        const { width, height } = this.props;
        const fixedWidth =
            (width || ReactMonacoEditor.defaultProps.width).toString().indexOf('%') !== -1 ? width : `${width}px`;
        const fixedHeight =
            (height || ReactMonacoEditor.defaultProps.height).toString().indexOf('%') !== -1 ? height : `${height}px`;
        const style = {
            width: fixedWidth,
            height: fixedHeight,
        };

        return <div ref={this.assignRef} style={style} className="react-monaco-editor-container" />;
    }

    private getOrCreateModel(modelId: string, value: string = '', language: string = 'kusto'): ModelAndView {
        const context = this.props.context || window;
        let model: monaco.editor.ITextModel | undefined;
        let modelAndView: ModelAndView | undefined;

        // Check component cache
        const modelWrapper = this.models[modelId];

        if (modelWrapper && modelWrapper.model) {
            // Use component cache
            modelAndView = modelWrapper;
        } else {
            // Check global monaco instance
            const existingModels = context.monaco.editor.getModels();
            model = existingModels.find((m: monaco.editor.ITextModel) => m.uri.path === modelId);
        }

        if (modelAndView) {
            // Found in cache, directly return
            return modelAndView;
        } else if (model) {
            // If found in global instance, update cache
            modelAndView = { model };
        } else {
            // Create a new model and update cache
            const modelUri = context.monaco.Uri.from({
                scheme: 'monaco',
                path: modelId,
            });
            model = context.monaco.editor.createModel(value, language, modelUri);
            modelAndView = { model: model! };
        }

        this.models[modelId] = modelAndView;

        return modelAndView;
    }

    /**
     * Since we want to preseve view state between tab switches (cursor position, ETC), we cache the models and
     * viewstates and retrieve them when needed.
     * @param prevProps previous props
     */
    private handleModelChange(prevProps: ReactMonacoEditorProps) {
        // if we don't have an editor we cannot persist or retrieve models and viewstates anyway
        if (!this.editor) {
            return;
        }

        const prevModelInfo = prevProps.modelInfo;
        const currentModelInfo = this.props.modelInfo;

        // store prev model and viewstate if we're switching to another model id, or switching
        // to a model managed by monaco (hence model info is empty).
        if (prevModelInfo && (!currentModelInfo || currentModelInfo.modelId !== prevModelInfo.modelId)) {
            const prevModel = this.editor.getModel();
            const prevViewState = this.editor.saveViewState();
            if (prevModel && prevViewState) {
                this.models[prevModelInfo.modelId] = {
                    model: prevModel,
                    viewState: prevViewState,
                };
            }
        }

        // If there's no modelInfo we have nothing to try and load from model/viewstate cache.
        if (!currentModelInfo) {
            return;
        }

        const { modelId, allModelIds } = currentModelInfo;

        // if we're not switching models we don't want to restore state.
        // why? beacuase the monaco model is the source of truth while editing the same editor model.
        if (prevModelInfo && currentModelInfo.modelId === prevModelInfo.modelId) {
            return;
        }

        const { model: nextModel, viewState: nextViewState } = this.getOrCreateModel(
            modelId,
            this.props.value,
            'kusto'
        );

        this.editor.setModel(nextModel);
        this.currentValue = nextModel.getValue(monaco.editor.EndOfLinePreference.LF);
        if (nextViewState) {
            this.editor.restoreViewState(nextViewState);
        }

        // When switching models, it usually means we're swithcing tabs. this causes monaco to lose focus.
        // Let's bring it back.
        this.editor.focus();

        // Delete cached models for deleted tabs.
        const cachedModelIds = Object.keys(this.models);
        const activeModels = new Set(allModelIds);
        const modelsToDelete = cachedModelIds.filter((m) => !activeModels.has(m));
        modelsToDelete.forEach((id) => {
            this.models[id].model.dispose();
            delete this.models[id];
        });
    }

    private handleTextChange(prevProps: ReactMonacoEditorProps) {
        if (
            this.props.value !== prevProps.value &&
            this.props.value !== this.currentValue &&
            this.props.value !== null
        ) {
            // Always refer to the latest value
            this.currentValue = this.props.value;
            // Consider the situation of rendering 1+ times before the editor mounted
            if (this.editor) {
                this.preventTriggerChangeEvent = true;
                this.editor.setValue(this.currentValue || '');
                this.preventTriggerChangeEvent = false;
            }
        }
    }

    private handleLanguageChange(prevProps: ReactMonacoEditorProps) {
        if (prevProps.language !== this.props.language && this.editor) {
            const context = this.props.context || window;
            context.monaco.editor.setModelLanguage(this.editor.getModel(), this.props.language);
        }
    }

    private async handleThemeChange(prevProps: ReactMonacoEditorProps) {
        await this.monacoEditorMountPromise;

        if (prevProps.theme !== this.props.theme) {
            const context = this.props.context || window;
            context.monaco.editor.setTheme(this.props.theme);
        }
    }

    private async handleSettingsChange(prevProps: ReactMonacoEditorProps) {
        await this.monacoEditorMountPromise;
        if (!this.editor) {
            return;
        }

        // options is defaulted to {} so it should never be undefined. thus we're assuming it's defined.
        if (prevProps.options!.mouseWheelZoom !== this.props.options!.mouseWheelZoom) {
            this.editor.updateOptions(this.props.options!);
        }
    }

    private afterViewInit() {
        const context = this.props.context || window;
        if (context.monaco !== undefined) {
            this.initMonaco();
            return;
        }
        const { requireConfig } = this.props;
        const loaderUrl = requireConfig!.url || 'vs/loader.js';
        const onGotAmdLoader = () => {
            if (context.__REACT_MONACO_EDITOR_LOADER_ISPENDING__) {
                // Do not use webpack
                if (requireConfig!.paths && requireConfig!.paths!.vs) {
                    context.require.config(requireConfig);
                }
            }

            // Load monaco
            context.require(['vs/editor/editor.main'], () => {
                context.require(['vs/language/kusto/monaco.contribution', 'vs/language/json/jsonMode'], () => {
                    this.initMonaco();
                });
            });

            // Call the delayed callbacks when AMD loader has been loaded
            if (context.__REACT_MONACO_EDITOR_LOADER_ISPENDING__) {
                context.__REACT_MONACO_EDITOR_LOADER_ISPENDING__ = false;
                const loaderCallbacks = context.__REACT_MONACO_EDITOR_LOADER_CALLBACKS__;
                if (loaderCallbacks && loaderCallbacks.length) {
                    let currentCallback = loaderCallbacks.shift();
                    while (currentCallback) {
                        currentCallback.fn.call(currentCallback.context);
                        currentCallback = loaderCallbacks.shift();
                    }
                }
            }
        };

        // Load AMD loader if necessary
        if (context.__REACT_MONACO_EDITOR_LOADER_ISPENDING__) {
            // We need to avoid loading multiple loader.js when there are multiple editors loading
            // concurrently, delay to call callbacks except the first one
            // eslint-disable-next-line max-len
            context.__REACT_MONACO_EDITOR_LOADER_CALLBACKS__ = context.__REACT_MONACO_EDITOR_LOADER_CALLBACKS__ || [];
            context.__REACT_MONACO_EDITOR_LOADER_CALLBACKS__.push({
                context: this,
                fn: onGotAmdLoader,
            });
        } else if (typeof context.require === 'undefined') {
            const loaderScript = context.document.createElement('script');
            loaderScript.type = 'text/javascript';
            loaderScript.src = loaderUrl;
            loaderScript.addEventListener('load', onGotAmdLoader);
            context.document.body.appendChild(loaderScript);
            context.__REACT_MONACO_EDITOR_LOADER_ISPENDING__ = true;
        } else {
            onGotAmdLoader();
        }
    }
    private initMonaco() {
        const value = this.props.value !== null ? this.props.value : this.props.defaultValue;
        const { language, theme, options, modelInfo: modelData } = this.props;
        const context = this.props.context || window;
        if (this.containerElement && typeof context.monaco !== 'undefined') {
            // Before initializing monaco editor
            this.editorWillMount(context.monaco);
            const model = modelData ? this.getOrCreateModel(modelData.modelId, value, language).model : undefined;

            this.editor = context.monaco.editor.create(this.containerElement, {
                value,
                model,
                language,
                ...options,
            });
            if (theme) {
                context.monaco.editor.setTheme(theme);
            }

            this.resolveMonacoEditorMountPromise!();
            // After initializing monaco editor
            this.editorDidMount(this.editor!, context.monaco);
        }
    }

    private delayedDestroyMonaco = _.debounce(
        // monaco json formatting have a timeout without verifying the editor still alive
        // in Query detail view, we create and destroy the editor + updating the content when
        // user move between cells, which leads to this rare exception.
        () => {
            if (typeof this.editor !== 'undefined') {
                this.editor.dispose();
            }
            Object.values(this.models).forEach(({ model }) => {
                if (model) {
                    model.dispose();
                }
            });
        },
        500
    );

    private assignRef = (component: any) => {
        this.containerElement = component;
    };
}
